import { Building2, Coins, Hash } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import type { CategoryBonusRule } from "@artisancode/api-types";

interface CurrentWorkspaceQuickConfigProps {
  workspaceName: string;
  bonusRules: CategoryBonusRule[];
  sequencePreview: string | undefined;
  onNavigateToBonusRules: () => void;
  onNavigateToNumberSequence: () => void;
}

export function CurrentWorkspaceQuickConfig({
  workspaceName,
  bonusRules,
  sequencePreview,
  onNavigateToBonusRules,
  onNavigateToNumberSequence,
}: CurrentWorkspaceQuickConfigProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {/* Card Bonus Rider */}
      <Card className="shadow-xs hover:border-primary/40 transition-colors">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600">
                <Coins className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">
                  Gaji & Bonus Rider
                </CardTitle>
                <CardDescription className="text-xs">
                  Komisi & target penjualan rider di toko ini
                </CardDescription>
              </div>
            </div>
            <Badge variant="secondary" className="font-mono text-xs">
              {bonusRules.length} Aturan Aktif
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Konfigurasikan batas minimum kuantitas penjualan per kategori menu
            dan nominal bonus per cup/item khusus untuk mitra rider di outlet
            ini.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={onNavigateToBonusRules}
            className="w-full gap-1.5 text-xs"
          >
            <Coins className="h-3.5 w-3.5 text-amber-500" />
            <span>Buka Konfigurasi Gaji & Bonus Rider</span>
          </Button>
        </CardContent>
      </Card>

      {/* Card Penomoran Transaksi */}
      <Card className="shadow-xs hover:border-primary/40 transition-colors">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
                <Hash className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">
                  Penomoran Transaksi
                </CardTitle>
                <CardDescription className="text-xs">
                  Auto-number sequence transaksi kasir
                </CardDescription>
              </div>
            </div>
            <Badge variant="secondary" className="font-mono text-xs">
              {sequencePreview}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Atur format prefix, periode reset (harian/bulanan), dan panjang
            digit nomor urut struk/penjualan otomatis outlet ini.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={onNavigateToNumberSequence}
            className="w-full gap-1.5 text-xs"
          >
            <Hash className="h-3.5 w-3.5 text-emerald-600" />
            <span>Buka Pengaturan Penomoran</span>
          </Button>
        </CardContent>
      </Card>

      {/* Card Multi-Branch Info */}
      <Card className="shadow-xs md:col-span-2 lg:col-span-1">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10 text-blue-600">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold">
                Lingkup Pengaturan
              </CardTitle>
              <CardDescription className="text-xs">
                Pengaturan Toko vs Pengaturan Global
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-xs text-muted-foreground">
          <div className="flex items-start gap-2">
            <span className="font-bold text-foreground">•</span>
            <span>
              <strong>Pengaturan Toko Ini:</strong> Memengaruhi transaksi,
              penomoran, dan bonus di outlet <em>{workspaceName}</em>.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold text-foreground">•</span>
            <span>
              <strong>Pengaturan Global:</strong> Mengatur seluruh cabang,
              peran/roles sistem, dan akun pengguna platform.
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
